// Curated set of font families the app can actually render — they're
// self-hosted via @fontsource in `src/fonts.ts`, which is imported globally in
// `main.tsx`, so these render anywhere in the app without extra loading.
//
// ÚNICA lista del catálogo: la comparten los pickers del editor de video, el
// editor de tipografías de la marca, Edición gráfica y el modo Anuncio. Para
// agregar una fuente: importá su css de @fontsource en `src/fonts.ts`, sumala
// acá — y sumala también al espejo del render (`remotion/src/fonts.ts`), que es
// un paquete aparte y no puede importar este archivo. Si te olvidás de esa
// segunda parte, `fontOptions.espejo.test.ts` falla: la fuente se vería en el
// preview y saldría con el fallback en el MP4.
// Ver ADR-2026-09-16-una-sola-lista-de-fuentes.

export type FontCategory = "sans" | "serif" | "display" | "handwriting" | "mono";

export const TEXT_FONT_OPTIONS: Array<{
  family: string;
  category: FontCategory;
  label?: string;
}> = [
  { family: "Inter", category: "sans" },
  { family: "Poppins", category: "sans" },
  { family: "Montserrat", category: "sans" },
  { family: "Roboto", category: "sans" },
  { family: "Playfair Display", category: "serif" },
  { family: "Bebas Neue", category: "display" },
  { family: "Oswald", category: "display" },
  { family: "Lobster", category: "handwriting" },
  { family: "Caveat", category: "handwriting" },
  { family: "Pacifico", category: "handwriting" },
  // Subtitle / viral-caption pack (TikTok / Reels / Shorts / MrBeast style).
  { family: "Anton", category: "display" },
  { family: "Fjalla One", category: "display" },
  { family: "Archivo Black", category: "display" },
  { family: "Russo One", category: "display" },
  { family: "Bangers", category: "display" },
  { family: "Luckiest Guy", category: "display" },
  { family: "Permanent Marker", category: "handwriting" },
  // ── Las 33 más usadas de Google Fonts ────────────────────────────────
  // Sans — el grueso del top de Google Fonts.
  { family: "Open Sans", category: "sans" },
  { family: "Noto Sans", category: "sans" },
  { family: "Lato", category: "sans" },
  { family: "Roboto Condensed", category: "sans" },
  { family: "Raleway", category: "sans" },
  { family: "Nunito", category: "sans" },
  { family: "Nunito Sans", category: "sans" },
  { family: "Ubuntu", category: "sans" },
  { family: "Rubik", category: "sans" },
  { family: "Work Sans", category: "sans" },
  { family: "Mulish", category: "sans" },
  { family: "DM Sans", category: "sans" },
  { family: "Quicksand", category: "sans" },
  { family: "Barlow", category: "sans" },
  { family: "Karla", category: "sans" },
  { family: "Josefin Sans", category: "sans" },
  { family: "Manrope", category: "sans" },
  { family: "Outfit", category: "sans" },
  // Serif — para lo editorial y lo que quiere leerse "serio".
  { family: "Merriweather", category: "serif" },
  { family: "Lora", category: "serif" },
  { family: "Roboto Slab", category: "serif" },
  { family: "PT Serif", category: "serif" },
  { family: "Libre Baskerville", category: "serif" },
  { family: "EB Garamond", category: "serif" },
  { family: "Cormorant Garamond", category: "serif" },
  // Display — títulos y placas.
  { family: "Abril Fatface", category: "display" },
  { family: "Teko", category: "display" },
  { family: "Righteous", category: "display" },
  { family: "Alfa Slab One", category: "display" },
  // Manuscritas.
  { family: "Dancing Script", category: "handwriting" },
  { family: "Shadows Into Light", category: "handwriting" },
  // Monoespaciadas.
  { family: "Roboto Mono", category: "mono" },
  { family: "Source Code Pro", category: "mono" },
];

// ── La lista como se ELIGE, y con qué fuente nace un texto ────────────────
// El array de arriba está agrupado por tanda (las de siempre, el pack de
// subtítulos, las 33 más usadas): sus comentarios cuentan de dónde salió cada
// grupo y el espejo del render compara ese orden. Pero para elegir una fuente
// entre 50, el único orden que sirve es el del abecedario — de ahí esta
// derivada, que es la que dibujan los pickers.
export const TEXT_FONT_OPTIONS_ALFABETICAS = [...TEXT_FONT_OPTIONS].sort(
  (a, b) => (a.label || a.family).localeCompare(b.label || b.family, "es"),
);

// Con qué fuente nace un texto nuevo: la PRIMERA de la lista alfabética, para
// que "el default" y "la primera de la lista" sean siempre lo mismo (decisión
// del usuario — ADR-2026-09-16-el-picker-de-fuentes-se-busca). Se calcula, no
// se escribe a mano: si mañana se suma una fuente que ordena antes, el default
// la sigue sin que haya que acordarse de tocar nada.
export const FUENTE_POR_DEFECTO =
  TEXT_FONT_OPTIONS_ALFABETICAS[0]?.family ?? "Inter";

// Build a CSS font-family value with a category-appropriate fallback so a
// preview never collapses to Times New Roman if the font is still loading or
// the user is offline.
export const fontStackFor = (family?: string): string => {
  const fam = family && family.trim() ? family.trim() : "Inter";
  const opt = TEXT_FONT_OPTIONS.find(
    (o) => o.family.toLowerCase() === fam.toLowerCase(),
  );
  const fallback = (() => {
    switch (opt?.category) {
      case "serif":
        return 'Georgia, "Times New Roman", serif';
      case "display":
        return '"Impact", "Arial Black", system-ui, sans-serif';
      case "handwriting":
        return '"Comic Sans MS", "Brush Script MT", cursive';
      case "mono":
        return "Menlo, Consolas, monospace";
      default:
        return 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
    }
  })();
  return `"${fam}", ${fallback}`;
};
